<script lang="ts">
    import type { Snippet } from "svelte";
    import Select from "svelte-select";
    import LL from "../../../i18n/i18n-svelte";
    import type { InputTagOption } from "./InputTagOption";
    import InfoButton from "./InfoButton.svelte";

    interface Props {
        optional?: boolean;
        label?: string;
        value?: InputTagOption[];
        options?: InputTagOption[];
        placeholder?: string;
        onfocus?: () => void;
        onblur?: () => void;
        onchange?: (value?: InputTagOption[]) => void;
        testId?: string;
        queryOptions?: (filterText: string) => Promise<{ value: string; label: string }[]>;
        info?: Snippet;
    }

    let {
        optional = false,
        label,
        value = $bindable<InputTagOption[] | undefined>(),
        options = [],
        placeholder,
        onfocus,
        onblur,
        onchange,
        testId,
        queryOptions,
        info,
    }: Props = $props();

    let filterText = $state("");

    // svelte-select renders its own internal input; give it a unique id so the
    // <label for> actually points at it (the hardcoded id="selector" never matched).
    const selectorId = "inputTagsSelector" + Math.random().toString(36).substring(2, 9);

    const visibleOptions = $derived.by(() => {
        const label = filterText.trim();

        if (label.length === 0) {
            return [];
        }

        const alreadySelected = value?.some((item) => item.label === label) ?? false;
        const alreadyAvailable = options.some((item) => item.label === label);

        if (alreadySelected || alreadyAvailable) {
            return options;
        }

        return [...options, { value: label, label, created: true }];
    });

    function _handleChange(event: CustomEvent<InputTagOption[] | undefined>) {
        value = event.detail;
        onchange?.(value);
    }

    // svelte-select renders its "clear all" button internally with no aria-label and no props/slot to
    // set one. It also mounts/unmounts as `value` changes, so a MutationObserver keeps the label applied.
    //
    // The per-tag remove control (.multi-item-clear) is worse: it's a plain <div> with only a
    // `pointerup` handler - no role, no tabindex, no aria-label, no keyboard support at all, and its
    // parent .multi-item has `role="none"`, so a tag is otherwise impossible to remove without a mouse.
    // Neither element is reachable via svelte-select's props/slots, so both are patched here directly.
    function accessibleSelectControls(node: HTMLElement) {
        function labelClearAllButton() {
            const btn = node.querySelector<HTMLButtonElement>(".clear-select");
            if (btn) {
                btn.setAttribute("aria-label", $LL.form.clearSelection());
            }
        }

        function makeTagClearButtonsAccessible() {
            node.querySelectorAll<HTMLElement>(".multi-item-clear").forEach((clearEl) => {
                const tagText =
                    clearEl.closest(".multi-item")?.querySelector(".multi-item-text")?.textContent?.trim() ?? "";
                clearEl.setAttribute("role", "button");
                clearEl.setAttribute("tabindex", "0");
                clearEl.setAttribute("aria-label", $LL.form.removeTag({ tag: tagText }));
            });
        }

        function apply() {
            labelClearAllButton();
            makeTagClearButtonsAccessible();
        }

        // Enter/Space activate the tag's remove control by replaying the pointerup svelte-select
        // itself listens for, since there's no way to call its internal handleMultiItemClear directly.
        function handleKeydown(event: KeyboardEvent) {
            const target = event.target as HTMLElement;
            if (!target.classList.contains("multi-item-clear")) {
                return;
            }
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                target.dispatchEvent(new PointerEvent("pointerup", { bubbles: true, cancelable: true }));
            }
        }

        apply();
        // svelte-select's own `.multi-item` wraps this control with
        // `on:keydown|preventDefault|stopPropagation`, which stops a bubble-phase listener here from
        // ever seeing the event. Listening on the capture phase runs before that stopPropagation fires.
        node.addEventListener("keydown", handleKeydown, true);
        const observer = new MutationObserver(apply);
        observer.observe(node, { childList: true, subtree: true });

        return {
            destroy() {
                observer.disconnect();
                node.removeEventListener("keydown", handleKeydown, true);
            },
        };
    }
</script>

<div class="flex flex-col" use:accessibleSelectControls>
    <div class="input-label mb-3" class:hidden={!label && !info && !optional}>
        {#if label}
            <label for={selectorId} class="relative grow text-mdark">
                {label}
            </label>
        {/if}

        {#if info}
            <InfoButton>
                {@render info()}
            </InfoButton>
        {/if}

        {#if optional}
            <div class="text-xs opacity-50">
                {$LL.form.optional()}
            </div>
        {/if}
    </div>
    <Select
        bind:filterText
        loadOptions={queryOptions}
        on:change={_handleChange}
        on:input={() => onchange?.(value)}
        on:select={() => onchange?.(value)}
        items={visibleOptions}
        bind:value
        multiple={true}
        placeholder={placeholder ?? $LL.form.selectTags()}
        on:focus={() => onfocus?.()}
        on:blur={() => onblur?.()}
        showChevron={true}
        listAutoWidth={false}
        --clear-select-color="var(--color-pop-red)"
        --input-color="var(--color-mdark)"
        --chevron-icon-colour="var(--color-mdark)"
        --internal-padding="0px"
        --height="48px"
        --multi-item-color="var(--color-mdark)"
        --multi-item-bg="var(--color-mlight-darker)"
        --multi-item-clear-icon-color="var(--color-mdark)"
        --multi-item-border-radius="6px"
        --multi-item-padding="8px"
        --multi-item-gap="6px"
        --multi-select-padding="3px 0 3px 0px"
        --multi-select-input-margin="0"
        --multi-item-outline="none"
        --padding="0px"
        --list-background="var(--color-mlight)"
        --list-empty-color="var(--color-mdark)"
        --selected-item-color="var(--color-mdark) !important"
        --selected-item-padding="0 0 0 32px"
        --list-border-radius="12px"
        --list-border="solid 1px var(--color-mlight-darker)"
        --list-empty-padding="12px"
        --item-color="var(--color-mdark-lighter)"
        --item-is-active-bg="var(--color-mlight-darker)"
        --item-is-active-color="var(--color-mdark)"
        --item-hover-bg="var(--color-mlight-darker)"
        --item-hover-color="var(--color-mdark)"
        --placeholder-color="var(--color-mdark-lighter)"
        --border="1px solid var(--color-mlight-darker)"
        --border-focused="1px solid var(--color-mblue)"
        --border-hover="1px solid var(--color-mblue-lighter)"
        inputStyles="box-shadow:none !important; margin:0; padding-left:8px"
        inputAttributes={{ "data-testid": testId, id: selectorId }}
        class="outline-none! w-full! bg-white!"
    >
        <div slot="item" let:item>
            {item.created ? $LL.notification.addNewTag({ tag: item.label }) : item.label}
        </div>
        <div slot="empty" class="empty">{$LL.form.noOptions()}</div>
    </Select>
</div>

<style>
    /* svelte-select's .multi-item hardcodes height/line-height (not padding-driven), which made the
       chip's horizontal padding look uneven next to its vertical spacing. Switching to auto height lets
       --multi-item-padding apply equally on all sides. */
    :global(.multi-item) {
        height: auto !important;
        line-height: normal !important;
        align-items: center;
    }

    /* .value-container's gap is hardcoded in svelte-select (no --css-var hook), so it's overridden here. */
    :global(.value-container) {
        gap: 5px 4px !important;
    }

    :global(.clear-select),
    :global(.multi-item-clear) {
        cursor: pointer !important;
    }

    :global(.multi-item-clear:focus-visible) {
        outline: 2px solid var(--color-mblue);
        outline-offset: 1px;
        border-radius: 2px;
    }

    /* The `slot="empty"` div is authored here, so it keeps THIS component's scoped class, not
       svelte-select's - its own `.empty { padding: var(--list-empty-padding, ...) }` rule never matches
       it, which left the "no options" text flush against the list edges. */
    :global(.empty) {
        padding: 12px !important;
        text-align: center;
        color: var(--color-mdark-lighter);
    }
</style>
